<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { Writable } from 'type-fest';
import type { ContactModel } from '@/components/fieldsets/StopContactFieldset.vue';
import type { DateTimeModel } from '@/components/fieldsets/StopDateTimeFieldset.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive, ref, watch } from 'vue';

import KMultipleAutocompleteField from '@/components/fields/KMultipleAutocompleteField.vue';
import KNumberField from '@/components/fields/KNumberField.vue';
import KSwitchField from '@/components/fields/KSwitchField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import SuburbAutocompleteField from '@/components/fields/SuburbAutocompleteField.vue';
import StopContactFieldset from '@/components/fieldsets/StopContactFieldset.vue';
import StopDateTimeFieldset from '@/components/fieldsets/StopDateTimeFieldset.vue';
import {
  formatStopDateTime,
  useStopDateTime,
} from '@/composables/jobEdit/useStopDateTime';
import useForm from '@/composables/useForm';
import { useJobEditAddressBookStore } from '@/stores/useJobEditAddressBookStore';
import { useJobEditStore } from '@/stores/useJobEditStore';
import { useStaticStore } from '@/stores/useStaticStore';
import { requiredValidationRule } from '@/helpers/validationRules';

export type InitialData = Writable<
  Partial<
    Pick<
      openapi._StopOpenapi,
      | 'stop_number'
      | 'time_earliest'
      | 'time_latest'
      | 'note_name'
      | 'note_phone'
      | 'note_text'
      | 'attributes'
      | 'is_warehouse'
      | 'driver_is'
      | 'arrived_at'
      | 'departed_at'
      | 'schedule_start'
      | 'schedule_finish'
      | 'pod_time'
    >
  >
> & {
  name?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  suburb?: string | null;
  post_code?: string | null;
  state?: string | null;
  duration?: number | null;
};

export type ValidData = Omit<
  InitialData,
  | 'driver_is'
  | 'arrived_at'
  | 'departed_at'
  | 'schedule_start'
  | 'schedule_finish'
  | 'pod_time'
>;

export interface Props {
  initialData?: InitialData;
  stopNumber?: number;
  stopType?: string;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
  stopNumber: 1,
  stopType: 'pickup',
  readonly: false,
});

const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const stopDateTimeInput = computed(() => ({
  state: formData.state ?? undefined,
  time_earliest: formData.time_earliest ?? null,
  time_latest: formData.time_latest ?? null,
}));

const { dateTimeModel, buildApiFields } = useStopDateTime(stopDateTimeInput);
const dateTimeData = ref<DateTimeModel>(dateTimeModel.value);

watch(dateTimeModel, (val: DateTimeModel) => {
  dateTimeData.value = val;
});

function buildSubmitData(): ValidData {
  const {
    driver_is: _driverIs,
    arrived_at: _arrivedAt,
    departed_at: _departedAt,
    schedule_start: _scheduleStart,
    schedule_finish: _scheduleFinish,
    pod_time: _podTime,
    time_earliest: _initialEarliest,
    time_latest: _initialLatest,
    ...rest
  } = formData;

  const apiFields = buildApiFields(dateTimeData.value);
  return {
    ...rest,
    stop_number: formData.stop_number ?? props.stopNumber,
    ...apiFields,
  };
}

const { form, handleSubmit } = useForm(props, formData, function (event) {
  emit('submit', event, buildSubmitData());
});

const isWarehouseStop = computed(() => formData.is_warehouse === true);

const contactModel = computed<ContactModel>({
  get() {
    return {
      note_name: formData.note_name ?? '',
      note_phone: formData.note_phone ?? '',
    };
  },
  set(value: ContactModel) {
    formData.note_name = value.note_name;
    formData.note_phone = value.note_phone;
  },
});

const jobEditStore = useJobEditStore();
const addressBookStore = useJobEditAddressBookStore();
const comboboxOpen = ref(false);
const savedAddress = computed<openapi.AddressGETOpenapi | null>(
  () => addressBookStore.items.find((a) => a.is_default) ?? null,
);

const addressBookItems = computed(() =>
  isWarehouseStop.value
    ? addressBookStore.items.filter((a) => a.warehouse)
    : addressBookStore.items.filter((a) => !a.warehouse),
);

const customerCode = computed(() => jobEditStore.jobData?.customer ?? null);

function applyAddressBookEntry(entry: openapi.AddressGETOpenapi): void {
  formData.name = entry.name;
  formData.address_line_1 = entry.line1 ?? null;
  formData.address_line_2 = entry.line2 ?? null;
  formData.suburb = entry.suburb?.name ?? null;
  formData.post_code = entry.suburb?.postcode ?? null;
  formData.state = entry.suburb?.state ?? null;
  formData.is_warehouse = entry.warehouse;
  formData.note_name = entry.note_name || null;
  formData.note_phone = entry.note_phone || null;
  formData.note_text = entry.note_text || null;
}

function loadDefaultAddress(): void {
  if (savedAddress.value) applyAddressBookEntry(savedAddress.value);
}

function selectSavedAddress(): void {
  loadDefaultAddress();
  comboboxOpen.value = false;
}

function clearAddressFields(): void {
  formData.address_line_1 = null;
  formData.address_line_2 = null;
  formData.suburb = null;
  formData.post_code = null;
  formData.state = null;
}

function handleClear(): void {
  clearAddressFields();
}

function handleWarehouseToggle(): void {
  formData.name = '';
  clearAddressFields();
}

const suburbAdapter = computed<openapi.SuburbOpenapi | null>({
  get() {
    if (formData.suburb) {
      return {
        id: 0,
        name: formData.suburb,
        state: formData.state || '',
        postcode: formData.post_code || '',
        zone: '',
        status: 0,
      };
    }
    return null;
  },
  set(value: openapi.SuburbOpenapi | null) {
    if (value?.name) {
      formData.suburb = value.name;

      if (value.postcode) {
        formData.post_code = value.postcode;
      }
      if (value.state) {
        formData.state = value.state;
      }
    } else {
      formData.suburb = null;
    }
    formData.address_line_1 = formData.address_line_1 || '';
  },
});

const staticStore = useStaticStore();

const driverDisplayText = computed<string>(() => {
  const code = formData.driver_is;
  if (!code) return '-';
  const driver = staticStore.getDrivers.find((d) => d.code === code);
  return driver ? `${driver.code}, ${driver.full_name}` : String(code);
});

const actualDuration = computed<string>(() => {
  const arrived = formData.arrived_at;
  const departed = formData.departed_at;
  if (!arrived || !departed) return '-';
  const diff = Math.round(
    (new Date(departed).getTime() - new Date(arrived).getTime()) / 60000,
  );
  return diff >= 0 ? String(diff) : '-';
});

interface AttributeItem {
  value: string;
  title: string;
  props: {
    subtitle?: string;
    titleIcon?: string;
  };
}

const attributeItemsForTemplate = computed<AttributeItem[]>(() => {
  return staticStore.getAttributes.map(
    (attr: { code?: string; description?: string; icon?: string }) => ({
      value: attr.code || '',
      title: attr.code || '',
      props: {
        subtitle: attr.description,
        titleIcon: attr.icon,
      },
    }),
  );
});

function handleNameChange(val: unknown): void {
  const name = typeof val === 'string' ? val : null;
  if (!name) return;
  const entry = addressBookItems.value.find((a) => a.name === name);
  if (entry) applyAddressBookEntry(entry);
}

function resetData(data: InitialData): void {
  Object.assign(formData, data);
  dateTimeData.value = dateTimeModel.value;
}

defineExpose({
  form,
  buildSubmitData,
  resetData,
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    :class="[$style.jobEditStopForm, { [$style.editing]: !props.readonly }]"
    validate-on="submit lazy"
    data-testid="jobEditStopForm"
    @submit.prevent="handleSubmit"
  >
    <!-- Address section -->
    <div :class="$style.formSection">
      <div :class="$style.addressHeader">
        <h5>Address</h5>
        <!-- :disabled, not :readonly: switches have no readonly state -->
        <KSwitchField
          v-model="formData.is_warehouse"
          label="KK WAREHOUSE"
          is-label-first
          is-small
          :disabled="props.readonly"
          @update:model-value="handleWarehouseToggle"
        />
      </div>
      <v-combobox
        v-model="formData.name"
        v-model:menu="comboboxOpen"
        label="Company name"
        :items="addressBookItems"
        item-title="name"
        item-value="name"
        :rules="[requiredValidationRule]"
        :loading="addressBookStore.loading"
        :readonly="props.readonly"
        variant="outlined"
        clearable
        hide-no-data
        :return-object="false"
        class="g-v-text-field"
        @update:model-value="handleNameChange"
        @click:clear="handleClear"
      >
        <template #prepend-item>
          <template v-if="!isWarehouseStop && savedAddress">
            <v-list-item :title="savedAddress.name" @click="selectSavedAddress">
              <template #append>
                <v-icon size="16">mdi-home-outline</v-icon>
              </template>
            </v-list-item>
            <v-divider />
          </template>
        </template>
        <template
          v-if="!isWarehouseStop && customerCode && !props.readonly"
          #append-inner="{ blur: blurCombobox }"
        >
          <v-btn
            icon
            variant="text"
            size="small"
            density="compact"
            title="Load default customer address"
            @mousedown.prevent
            @click="
              () => {
                blurCombobox();
                loadDefaultAddress();
              }
            "
          >
            <v-icon size="24">mdi-home-outline</v-icon>
          </v-btn>
        </template>
      </v-combobox>

      <div :class="$style.addressRow">
        <KTextField
          v-model="formData.address_line_2"
          label="Street address"
          :rules="[requiredValidationRule]"
          :readonly="props.readonly || isWarehouseStop"
          validate-on="input"
        />
        <KTextField
          v-model="formData.address_line_1"
          label="Room / Level / Unit"
          :readonly="props.readonly || isWarehouseStop"
        />
      </div>
      <div :class="$style.suburbRow">
        <SuburbAutocompleteField
          v-model="suburbAdapter"
          label="Suburb"
          :rules="[requiredValidationRule]"
          :class="$style.suburb"
          :readonly="props.readonly || isWarehouseStop"
          validate-on="input"
        />
      </div>
    </div>

    <!-- Contact details section -->
    <div :class="$style.formSection">
      <h5>Contact details</h5>
      <StopContactFieldset
        v-model="contactModel"
        :isWarehouseStop="isWarehouseStop"
        :readonly="props.readonly"
      />
      <KTextareaField
        v-model="formData.note_text"
        label="Collection instructions"
        :readonly="props.readonly"
        rows="3"
      />
      <h5>Driver</h5>
      <KTextField :model-value="driverDisplayText" :disabled="true" />
    </div>

    <!-- Time window section -->
    <div :class="$style.formSection">
      <StopDateTimeFieldset v-model="dateTimeData" :readonly="props.readonly" />

      <div :class="$style.timeGrid">
        <KTextField
          label="Arrive"
          :model-value="
            formatStopDateTime(
              formData.arrived_at,
              dateTimeData.time_earliest_date,
              formData.state,
            )
          "
          :readonly="true"
        />
        <KTextField
          label="Depart"
          :model-value="
            formatStopDateTime(
              formData.departed_at,
              dateTimeData.time_earliest_date,
              formData.state,
            )
          "
          :readonly="true"
        />
        <KTextField
          label="Schedule Start"
          :model-value="
            formatStopDateTime(
              formData.schedule_start,
              dateTimeData.time_earliest_date,
              formData.state,
            )
          "
          :readonly="true"
        />
        <KTextField
          label="Schedule Finish"
          :model-value="
            formatStopDateTime(
              formData.schedule_finish,
              dateTimeData.time_earliest_date,
              formData.state,
            )
          "
          :readonly="true"
        />
        <KTextField
          :class="$style.timeGridFull"
          label="POD Time"
          :model-value="
            formatStopDateTime(
              formData.pod_time,
              dateTimeData.time_earliest_date,
              formData.state,
            )
          "
          :readonly="true"
        />
      </div>

      <div :class="$style.durationRow">
        <KNumberField
          v-model="formData.duration"
          label="Planned Duration (min)"
          :readonly="props.readonly"
        />
        <KTextField
          label="Actual Duration (min)"
          :model-value="actualDuration"
          :readonly="true"
        />
      </div>
    </div>

    <!-- Attributes section -->
    <div :class="$style.formSection">
      <h5>Attributes</h5>
      <KMultipleAutocompleteField
        v-model="formData.attributes"
        :readonly="props.readonly"
        :items="attributeItemsForTemplate"
      />
    </div>
    <input type="submit" class="hidden" :disabled="props.readonly" />
  </v-form>
</template>

<style module lang="scss">
@use '@/styles/colors';

.formSection {
  margin-bottom: 12px;

  h5 {
    font-weight: 500;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-top: 8px;
    margin-bottom: 6px;
    color: colors.$color-gray-500;
  }
}

.addressHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h5 {
    margin-bottom: 0;
  }
}

.addressRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
}

.suburbRow {
  display: flex;
  gap: 12px;
  margin-top: 12px;

  .suburb {
    flex: 2;
  }
}

.timeGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
  margin-bottom: 8px;
}

.timeGridFull {
  grid-column: 1 / -1;
}

.durationRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
  margin-top: 8px;
}
</style>
